import { api, ApiError } from './api';
import { Event, CreateEventDto, UpdateEventDto } from '../types/event';

interface EventResponse {
  id: string;
  imageUrl: string;
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  option1?: string;
  option2?: string;
  option3?: string;
  createdAt: string;
  updatedAt: string;
}

interface EventsListApiResponse {
  events: EventResponse[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface EventsListResponse {
  events: Event[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface GetEventsParams {
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
}

const mapEventResponse = (event: EventResponse): Event => ({
  ...event,
  image: event.imageUrl ? event.imageUrl : '',
});

const encodeHTML = (str: string = '') => {
  return str.replace(/[&<>"']/g, (match) => {
    const entities: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return entities[match];
  });
};

export const events = {
  getList: async (params: GetEventsParams): Promise<EventsListResponse> => {
    try {
      const response = await api.get<EventsListApiResponse>('/events', { params });
      return {
        ...response,
        events: response.events.map(mapEventResponse),
      };
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },

  getById: async (id: string): Promise<Event> => {
    try {
      const response = await api.get<EventResponse>(`/events/${id}`);
      return mapEventResponse(response);
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },

  create: async (data: CreateEventDto): Promise<Event> => {
    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description);
      formData.append('startDate', data.startDate.toLocaleDateString('en-CA'));
      formData.append('endDate', data.endDate.toLocaleDateString('en-CA'));
      formData.append('option1', encodeHTML(data.option1 || ''));
      formData.append('option2', encodeHTML(data.option2 || ''));
      formData.append('option3', encodeHTML(data.option3 || ''));
      if (data.image) {
        formData.append('image', data.image);
      }

      const response = await api.post('/events', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return mapEventResponse(response as EventResponse);
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },

  update: async (id: string, data: UpdateEventDto): Promise<Event> => {
    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description);
      formData.append('startDate', data.startDate.toLocaleDateString('en-CA'));
      formData.append('endDate', data.endDate.toLocaleDateString('en-CA'));
      formData.append('option1', encodeHTML(data.option1 || ''));
      formData.append('option2', encodeHTML(data.option2 || ''));
      formData.append('option3', encodeHTML(data.option3 || ''));
      if (data.image) {
        formData.append('image', data.image);
      }

      const response = await api.patch(`/events/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return mapEventResponse(response as EventResponse);
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/events/${id}`);
    } catch (error) {
      const apiError = error as ApiError;
      throw apiError;
    }
  },
};
